import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

const STORAGE_PREFIX = 'newsletter-popup-dismissed';

/**
 * Newsletter signup popup — opens after delay and/or scroll depth (whichever comes first).
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends {DialogComponent}
 */
class NewsletterPopupComponent extends DialogComponent {
  /** @type {ReturnType<typeof setTimeout> | null} */
  #openTimer = null;

  /** @type {(() => void) | null} */
  #scrollHandler = null;

  /** @type {boolean} */
  #triggered = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('data-disabled') && !window.Shopify?.designMode) return;

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDismiss);

    if (this.hasAttribute('data-subscribed') || this.hasAttribute('data-form-error')) {
      if (this.hasAttribute('data-subscribed')) {
        this.#setDismissed();
      }

      this.#scheduleOpen(300);
      return;
    }

    if (window.Shopify?.designMode) {
      this.#scheduleOpen(500);
      return;
    }

    if (this.#shouldSkip()) return;

    this.#bindTriggers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanupTriggers();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDismiss);
  }

  #bindTriggers() {
    const delaySeconds = Number(this.dataset.delay || 9);
    if (delaySeconds > 0) {
      this.#openTimer = setTimeout(() => this.#tryOpen(), delaySeconds * 1000);
    }

    const scrollPercent = Number(this.dataset.scrollPercent || 0);
    if (scrollPercent > 0) {
      this.#scrollHandler = () => {
        const scrollable = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollable <= 0) return;

        const scrolled = (window.scrollY / scrollable) * 100;
        if (scrolled >= scrollPercent) {
          this.#tryOpen();
        }
      };

      window.addEventListener('scroll', this.#scrollHandler, { passive: true });
      this.#scrollHandler();
    }
  }

  #cleanupTriggers() {
    if (this.#openTimer) {
      clearTimeout(this.#openTimer);
      this.#openTimer = null;
    }

    if (this.#scrollHandler) {
      window.removeEventListener('scroll', this.#scrollHandler);
      this.#scrollHandler = null;
    }
  }

  /**
   * @param {number} ms
   */
  #scheduleOpen(ms) {
    this.#openTimer = setTimeout(() => this.#tryOpen(), ms);
  }

  #tryOpen() {
    if (this.#triggered || this.refs.dialog?.open) return;

    this.#triggered = true;
    this.#cleanupTriggers();
    this.showDialog();
  }

  #handleDismiss = () => {
    this.#setDismissed();
  };

  #setDismissed() {
    const frequency = this.dataset.frequency || 'once_7_days';

    if (frequency === 'every_visit') return;

    if (frequency === 'once_session') {
      sessionStorage.setItem(this.#storageKey, '1');
      return;
    }

    localStorage.setItem(this.#storageKey, String(Date.now()));
  }

  #shouldSkip() {
    const frequency = this.dataset.frequency || 'once_7_days';
    const showOn = this.dataset.showOn || 'all';

    if (showOn === 'home' && this.dataset.template !== 'index') return true;
    if (showOn === 'product' && this.dataset.template !== 'product') return true;

    if (frequency === 'every_visit') return false;

    if (frequency === 'once_session') {
      return sessionStorage.getItem(this.#storageKey) === '1';
    }

    const dismissedAt = localStorage.getItem(this.#storageKey);
    if (!dismissedAt) return false;

    const days = this.#getDismissDays();
    const elapsed = Date.now() - Number(dismissedAt);
    return elapsed < days * 24 * 60 * 60 * 1000;
  }

  #getDismissDays() {
    const frequency = this.dataset.frequency || 'once_7_days';

    if (frequency === 'once_30_days') return 30;
    if (frequency === 'once_7_days') return 7;

    return 7;
  }

  get #storageKey() {
    return `${STORAGE_PREFIX}-${this.dataset.storageKey || 'default'}`;
  }
}

if (!customElements.get('newsletter-popup-component')) {
  customElements.define('newsletter-popup-component', NewsletterPopupComponent);
}
